import { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';
import { Box, BoxPair, Tip } from './Guidance.types';

export function useGuidance(items: Tip[]) {
  const { width, height } = useWindowSize();
  const [pairs, setPairs] = useState<BoxPair[]>([]);

  useEffect(() => {
    const val = items.map((item) => getPair(item)).filter(Boolean) as BoxPair[];
    setPairs(val);
  }, [items, width, height]);

  return pairs;
}

export const getPair = (item: Tip): BoxPair | null => {
  try {
    const { position, title, className } = item;
    const target = document.querySelector(`.${className}`) as HTMLElement | null;

    if (!target) {
      console.warn(`Element with class "${className}" not found.`);
      return null;
    }

    const box = target.getBoundingClientRect();

    const targetBox: Box = {
      x: box.left,
      y: box.top,
      width: box.width,
      height: box.height,
    };

    // Guidance box dimensions (adjust based on content)
    const guidanceWidth = Math.max(150, title.length * 9);
    const guidanceHeight = 60;
    const gap = 30; // Space between target and guidance

    // Arrow dimensions
    const arrowSize = 30;

    let guidanceBox: Box = { x: 0, y: 0, width: guidanceWidth, height: guidanceHeight };
    let arrowBox: Box = { x: 0, y: 0, width: arrowSize, height: arrowSize };

    switch (position) {
      case 'bottom right':
        guidanceBox = {
          x: targetBox.x + targetBox.width + gap + 20,
          y: targetBox.y + targetBox.height + 30,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x + targetBox.width + gap - arrowSize,
          y: targetBox.y + targetBox.height - 5,
          width: arrowSize,
          height: arrowSize,
        };
        break;
      case 'left':
        guidanceBox = {
          x: targetBox.x - guidanceWidth - gap,
          y: targetBox.y + targetBox.height / 2 - guidanceHeight / 2,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x - arrowSize - gap,
          y: targetBox.y + targetBox.height / 2 - arrowSize / 2 - 10,
          width: arrowSize,
          height: arrowSize,
        };
        break;
      case 'bottom left':
        guidanceBox = {
          x: targetBox.x - 60,
          y: targetBox.y + targetBox.height + gap + 50,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x + 5,
          y: targetBox.y + targetBox.height + gap - 5,
          width: arrowSize,
          height: arrowSize,
        };
        break;
      case 'top':
        guidanceBox = {
          x: targetBox.x + targetBox.width / 2 - guidanceWidth / 2,
          y: targetBox.y - guidanceHeight - gap,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x + targetBox.width / 2 - arrowSize / 2,
          y: targetBox.y - arrowSize - gap,
          width: arrowSize,
          height: arrowSize,
        };
        break;
      case 'top left':
        guidanceBox = {
          x: targetBox.x - 180,
          y: targetBox.y - guidanceHeight - gap,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x - 60,
          y: targetBox.y - arrowSize - gap + 20,
          width: arrowSize,
          height: arrowSize,
        };
        break;
      case 'right':
        guidanceBox = {
          x: targetBox.x + targetBox.width + gap + 50,
          y: targetBox.y + targetBox.height / 2 - guidanceHeight / 2 - 20,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x + targetBox.width + gap - 5,
          y: targetBox.y + targetBox.height / 2 - arrowSize / 2 - 60,
          width: arrowSize,
          height: arrowSize,
        };
        break;
      case 'top right':
        guidanceBox = {
          x: targetBox.x + targetBox.width + gap + 20,
          y: targetBox.y + targetBox.height / 2 - guidanceHeight / 2 - 50,
          width: guidanceWidth,
          height: guidanceHeight,
        };
        arrowBox = {
          x: targetBox.x + targetBox.width + gap - 45,
          y: targetBox.y + targetBox.height / 2 - arrowSize / 2 - 40,
          width: arrowSize,
          height: arrowSize,
        };
        break;
    }

    return { item, target: targetBox, arrow: arrowBox, guidance: guidanceBox };
  } catch (err) {
    console.error('Error in getPair', err);
    return null;
  }
};
